/* Atlas v3 — News reader sheet (mobile). Opens IN-APP when a Wire news row is
   tapped (never a chart). Shows the provider blurb (news_articles.summary) — we
   do NOT generate or display the publisher's article body; the source link is
   attribution / escape hatch only.

   Render order: image · ticker circle-logo + source + timestamp · headline ·
   summary. Fallback: no summary → headline + source link only.
   Mirrors PickSheet shell/motion. */
import { useState, useEffect } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'

export function NewsSheet({ item, onClose, onTicker, desktop = false }) {
  const [shown, setShown] = useState(false)
  const [imgBroken, setImgBroken] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  if (!item) return null

  const { t: ticker, why: source, time, headline, summary, image, url } = item
  let host = null
  try { host = url ? new URL(url).hostname.replace(/^www\./, '') : null } catch { host = null }
  const sourceLabel = source || host || 'source'

  // Desktop: a centered modal card (matches the Insights article reader), not
  // the phone bottom-sheet — fades/lifts in, rounded on all corners, no drag
  // handle. Mobile: unchanged slide-up sheet.
  const panelStyle = desktop
    ? { position: 'relative', width: 'min(680px, 94vw)', maxHeight: '86vh', margin: '7vh auto 0', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', border: '1px solid hsl(var(--studio-border))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: 'var(--studio-radius-lg)',
        boxShadow: '0 32px 80px -24px hsl(var(--studio-overlay) / 0.9)',
        opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 220ms ease, transform 220ms var(--studio-ease)', overflow: 'hidden' }
    : { position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={panelStyle}>
        <div style={{ padding: desktop ? '16px 20px 0' : '9px 20px 0', flexShrink: 0 }}>
          {!desktop && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))' }}>ON THE WIRE</span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
        </div>

        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 30px' }}>
          {/* image — branded placeholder when the article has none (or its image
              404s, e.g. some Yahoo wire items) so the card still reads as
              intentional instead of an empty gap / broken thumbnail */}
          {image && !imgBroken ? (
            <img src={image} alt="" loading="lazy"
              onError={() => setImgBroken(true)}
              style={{ width: '100%', height: 168, objectFit: 'cover', borderRadius: 'var(--studio-radius)', marginBottom: 14, border: '1px solid hsl(var(--studio-border) / 0.5)' }} />
          ) : (
            <div style={{ width: '100%', height: 140, marginBottom: 14, borderRadius: 'var(--studio-radius)', border: '1px solid hsl(var(--studio-border) / 0.5)',
              background: 'radial-gradient(120% 150% at 22% -10%, hsl(var(--studio-surface-2)) 0%, hsl(var(--studio-background)) 72%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <StockLogo ticker={ticker} size={46} rounded="50%" />
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.24em', color: 'hsl(var(--studio-accent))' }}>ON THE WIRE</span>
            </div>
          )}

          {/* ticker logo + source + timestamp */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
            <span onClick={() => ticker && onTicker?.(ticker)} style={{ cursor: ticker ? 'pointer' : 'default', lineHeight: 0 }}>
              <StockLogo ticker={ticker} size={26} rounded="50%" />
            </span>
            <span onClick={() => ticker && onTicker?.(ticker)} style={{ cursor: ticker ? 'pointer' : 'default', fontFamily: 'var(--studio-font-mono)', fontSize: 12.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text))' }}>{ticker}</span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))' }}>
              {sourceLabel}{time ? ` · ${time}` : ''}
            </span>
          </div>

          {/* headline */}
          <div style={{ fontFamily: 'var(--studio-font-display)', fontSize: 21, lineHeight: 1.28, color: 'hsl(var(--studio-text))', marginBottom: summary ? 12 : 16, textWrap: 'pretty' }}>{headline}</div>

          {/* provider blurb */}
          {summary && (
            <p style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 14, lineHeight: 1.65, color: 'hsl(var(--studio-text-muted))', margin: 0 }}>{summary}</p>
          )}

          {/* source link — attribution / escape hatch */}
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: 18, fontFamily: 'var(--studio-font-mono)', fontSize: 11, letterSpacing: '0.04em', color: 'hsl(var(--studio-accent))', textDecoration: 'none' }}>
              Read full at {sourceLabel} ↗
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
